import { lazy } from "react";

/**
 * Wrapper around React.lazy that handles ChunkLoadError gracefully.
 * After a deploy, old chunks are removed from the server. Users with
 * stale tabs will fail to load them. This wrapper:
 *   1. Retries the import once
 *   2. If still failing, forces a page reload (once) to fetch fresh assets
 */
const lazyWithRetry = (componentImport) =>
  lazy(() =>
    componentImport().catch((error) => {
      // Retry once
      return componentImport().catch((retryError) => {
        // If it's a chunk load error, reload the page (once)
        const msg = retryError.message || "";
        if (
          retryError.name === "ChunkLoadError" ||
          msg.includes("Loading chunk") ||
          msg.includes("Failed to fetch dynamically imported module") ||
          msg.includes("Unexpected token") ||
          (retryError instanceof TypeError && /^.{1,2} is not a (function|constructor)/.test(msg))
        ) {
          const hasReloaded = sessionStorage.getItem("chunkErrorReloaded");
          if (!hasReloaded) {
            sessionStorage.setItem("chunkErrorReloaded", "true");
            window.location.reload();
            // Return a never-resolving promise to prevent rendering while reloading
            return new Promise(() => {});
          }
          // Already reloaded once, clear flag for next time and let error propagate
          sessionStorage.removeItem("chunkErrorReloaded");
        }
        throw retryError;
      });
    })
  );

export default lazyWithRetry;
